import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface SourceStatus {
  configured: boolean;
  error: string | null;
}

interface Sources {
  shopify: SourceStatus;
  meta: SourceStatus;
  google: SourceStatus;
  gorgias: SourceStatus;
  linear: SourceStatus;
  sentry: SourceStatus;
  facebook: SourceStatus;
  instagram: SourceStatus;
  tiktok: SourceStatus;
  youtube: SourceStatus;
}

type ApiTab = 'shopify' | 'meta' | 'google' | 'gorgias' | 'linear' | 'sentry' | 'facebook' | 'instagram' | 'tiktok' | 'youtube';

const TABS: Array<{ id: ApiTab; label: string }> = [
  { id: 'shopify', label: 'Shopify' },
  { id: 'meta', label: 'Meta' },
  { id: 'google', label: 'Google' },
  { id: 'gorgias', label: 'Gorgias' },
  { id: 'linear', label: 'Linear' },
  { id: 'sentry', label: 'Sentry' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
];

const sourceLabel = (source?: SourceStatus) => {
  if (!source) return 'not configured';
  if (!source.configured) return 'not configured';
  return source.error || 'connected';
};

const defaultTab = (sources: Sources): ApiTab => {
  if (!sources.shopify.configured) return 'shopify';
  if (!sources.meta.configured) return 'meta';
  if (!sources.google?.configured) return 'google';
  if (!sources.gorgias?.configured) return 'gorgias';
  if (!sources.linear?.configured) return 'linear';
  if (!sources.sentry?.configured) return 'sentry';
  if (!sources.facebook?.configured) return 'facebook';
  if (!sources.instagram?.configured) return 'instagram';
  if (!sources.tiktok?.configured) return 'tiktok';
  if (!sources.youtube?.configured) return 'youtube';
  return 'shopify';
};

const StoreDashboardApisView: React.FC = () => {
  const [sources, setSources] = useState<Sources | null>(null);
  const [shop, setShop] = useState('fluora-store.myshopify.com');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [authorizeUrl, setAuthorizeUrl] = useState<string | null>(null);
  const [metaToken, setMetaToken] = useState('');
  const [metaAccountId, setMetaAccountId] = useState('');
  const [metaAccounts, setMetaAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [metaConnecting, setMetaConnecting] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [googleCustomerId, setGoogleCustomerId] = useState('');
  const [googleLoginCustomerId, setGoogleLoginCustomerId] = useState('');
  const [googleDeveloperToken, setGoogleDeveloperToken] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [googleRefreshToken, setGoogleRefreshToken] = useState('');
  const [googleAccounts, setGoogleAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [gorgiasDomain, setGorgiasDomain] = useState('fluora');
  const [gorgiasEmail, setGorgiasEmail] = useState('');
  const [gorgiasApiKey, setGorgiasApiKey] = useState('');
  const [gorgiasConnecting, setGorgiasConnecting] = useState(false);
  const [gorgiasError, setGorgiasError] = useState<string | null>(null);
  const [linearApiKey, setLinearApiKey] = useState('');
  const [linearConnecting, setLinearConnecting] = useState(false);
  const [linearError, setLinearError] = useState<string | null>(null);
  const [sentryToken, setSentryToken] = useState('');
  const [sentryOrg, setSentryOrg] = useState('');
  const [sentryOrgs, setSentryOrgs] = useState<Array<{ slug: string; name: string }>>([]);
  const [sentryConnecting, setSentryConnecting] = useState(false);
  const [sentryError, setSentryError] = useState<string | null>(null);
  const [facebookToken, setFacebookToken] = useState('');
  const [facebookPageId, setFacebookPageId] = useState('');
  const [facebookPages, setFacebookPages] = useState<Array<{ id: string; name: string; igUsername?: string }>>([]);
  const [facebookConnecting, setFacebookConnecting] = useState(false);
  const [facebookError, setFacebookError] = useState<string | null>(null);
  const [tiktokToken, setTiktokToken] = useState('');
  const [tiktokConnecting, setTiktokConnecting] = useState(false);
  const [tiktokError, setTiktokError] = useState<string | null>(null);
  const [youtubeClientId, setYoutubeClientId] = useState('');
  const [youtubeClientSecret, setYoutubeClientSecret] = useState('');
  const [youtubeRefreshToken, setYoutubeRefreshToken] = useState('');
  const [youtubeChannelId, setYoutubeChannelId] = useState('');
  const [youtubeChannels, setYoutubeChannels] = useState<Array<{ id: string; name: string }>>([]);
  const [youtubeConnecting, setYoutubeConnecting] = useState(false);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [tab, setTab] = useState<ApiTab>('shopify');
  const [tabReady, setTabReady] = useState(false);

  const load = useCallback(async () => {
    const response = await axios.get(`${API_BASE_URL}/store-dashboard/summary`, {
      params: { days: 7 },
      withCredentials: true,
    });
    setSources(response.data.sources);
    if (!tabReady) {
      setTab(defaultTab(response.data.sources));
      setTabReady(true);
    }
  }, [tabReady]);

  useEffect(() => {
    load().catch(() => setSources({
      shopify: { configured: false, error: null },
      meta: { configured: false, error: null },
      google: { configured: false, error: null },
      gorgias: { configured: false, error: null },
      linear: { configured: false, error: null },
      sentry: { configured: false, error: null },
      facebook: { configured: false, error: null },
      instagram: { configured: false, error: null },
      tiktok: { configured: false, error: null },
      youtube: { configured: false, error: null },
    }));
  }, [load]);

  const connectShopify = async (event: React.FormEvent) => {
    event.preventDefault();
    setConnecting(true);
    setConnectError(null);
    setAuthorizeUrl(null);
    try {
      await axios.post(`${API_BASE_URL}/store-dashboard/connect`, {
        shop,
        clientId,
        clientSecret,
      }, { withCredentials: true });
      await load();
    } catch (err: any) {
      setConnectError(err.response?.data?.message || 'Could not connect Shopify');
      setAuthorizeUrl(err.response?.data?.authorizeUrl || null);
    } finally {
      setConnecting(false);
    }
  };

  const connectMeta = async (event: React.FormEvent) => {
    event.preventDefault();
    setMetaConnecting(true);
    setMetaError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/store-dashboard/meta/connect`, {
        accessToken: metaToken,
        accountId: metaAccountId,
      }, { withCredentials: true });
      if (response.data?.accounts?.length) {
        setMetaAccounts(response.data.accounts);
        if (!metaAccountId) setMetaAccountId(response.data.accounts[0].id);
        return;
      }
      setMetaAccounts([]);
      await load();
    } catch (err: any) {
      setMetaError(err.response?.data?.message || 'Could not connect Meta');
    } finally {
      setMetaConnecting(false);
    }
  };

  const connectGoogle = async (event: React.FormEvent) => {
    event.preventDefault();
    setGoogleConnecting(true);
    setGoogleError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/store-dashboard/google/connect`, {
        customerId: googleCustomerId,
        loginCustomerId: googleLoginCustomerId,
        developerToken: googleDeveloperToken,
        clientId: googleClientId,
        clientSecret: googleClientSecret,
        refreshToken: googleRefreshToken,
      }, { withCredentials: true });
      if (response.data?.accounts?.length && !response.data.configured) {
        setGoogleAccounts(response.data.accounts);
        setGoogleCustomerId(response.data.accounts[0].id);
        if (response.data.managerId) setGoogleLoginCustomerId(response.data.managerId);
        return;
      }
      setGoogleAccounts([]);
      await load();
    } catch (err: any) {
      setGoogleError(err.response?.data?.message || 'Could not connect Google Ads');
    } finally {
      setGoogleConnecting(false);
    }
  };

  const connectGorgias = async (event: React.FormEvent) => {
    event.preventDefault();
    setGorgiasConnecting(true);
    setGorgiasError(null);
    try {
      await axios.post(`${API_BASE_URL}/store-dashboard/gorgias/connect`, {
        domain: gorgiasDomain,
        email: gorgiasEmail,
        apiKey: gorgiasApiKey,
      }, { withCredentials: true });
      await load();
    } catch (err: any) {
      setGorgiasError(err.response?.data?.message || 'Could not connect Gorgias');
    } finally {
      setGorgiasConnecting(false);
    }
  };

  const connectSentry = async (event: React.FormEvent) => {
    event.preventDefault();
    setSentryConnecting(true);
    setSentryError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/store-dashboard/sentry/connect`, {
        authToken: sentryToken,
        org: sentryOrg,
      }, { withCredentials: true });
      if (response.data?.organizations?.length && !response.data.configured) {
        setSentryOrgs(response.data.organizations);
        if (!sentryOrg) setSentryOrg(response.data.organizations[0].slug);
        return;
      }
      setSentryOrgs([]);
      await load();
    } catch (err: any) {
      setSentryError(err.response?.data?.message || 'Could not connect Sentry');
    } finally {
      setSentryConnecting(false);
    }
  };

  const connectFacebook = async (event: React.FormEvent) => {
    event.preventDefault();
    setFacebookConnecting(true);
    setFacebookError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/store-dashboard/facebook/connect`, {
        accessToken: facebookToken,
        pageId: facebookPageId,
      }, { withCredentials: true });
      if (response.data?.pages?.length && !response.data.configured) {
        setFacebookPages(response.data.pages);
        if (!facebookPageId) setFacebookPageId(response.data.pages[0].id);
        return;
      }
      setFacebookPages([]);
      await load();
    } catch (err: any) {
      setFacebookError(err.response?.data?.message || 'Could not connect Facebook');
    } finally {
      setFacebookConnecting(false);
    }
  };

  const connectTikTok = async (event: React.FormEvent) => {
    event.preventDefault();
    setTiktokConnecting(true);
    setTiktokError(null);
    try {
      await axios.post(`${API_BASE_URL}/store-dashboard/tiktok/connect`, {
        accessToken: tiktokToken,
      }, { withCredentials: true });
      await load();
    } catch (err: any) {
      setTiktokError(err.response?.data?.message || 'Could not connect TikTok');
    } finally {
      setTiktokConnecting(false);
    }
  };

  const connectYouTube = async (event: React.FormEvent) => {
    event.preventDefault();
    setYoutubeConnecting(true);
    setYoutubeError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/store-dashboard/youtube/connect`, {
        clientId: youtubeClientId,
        clientSecret: youtubeClientSecret,
        refreshToken: youtubeRefreshToken,
        channelId: youtubeChannelId,
      }, { withCredentials: true });
      if (response.data?.channels?.length && !response.data.configured) {
        setYoutubeChannels(response.data.channels);
        if (!youtubeChannelId) setYoutubeChannelId(response.data.channels[0].id);
        return;
      }
      setYoutubeChannels([]);
      await load();
    } catch (err: any) {
      setYoutubeError(err.response?.data?.message || 'Could not connect YouTube');
    } finally {
      setYoutubeConnecting(false);
    }
  };

  const connectLinear = async (event: React.FormEvent) => {
    event.preventDefault();
    setLinearConnecting(true);
    setLinearError(null);
    try {
      await axios.post(`${API_BASE_URL}/store-dashboard/linear/connect`, {
        apiKey: linearApiKey,
      }, { withCredentials: true });
      await load();
    } catch (err: any) {
      setLinearError(err.response?.data?.message || 'Could not connect Linear');
    } finally {
      setLinearConnecting(false);
    }
  };

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">Integrations</div>
          <h1>APIs</h1>
        </div>
      </div>

      <div className="store-dash-api-layout">
      <div className="store-dash-tabs" role="tablist" aria-orientation="vertical">
        {TABS.map((item) => {
          const source = sources?.[item.id];
          const on = Boolean(source?.configured && !source?.error);
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`store-dash-tab${tab === item.id ? ' is-active' : ''}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              <span className={on ? 'is-on' : 'is-off'}>{sourceLabel(source)}</span>
            </button>
          );
        })}
      </div>

      <div className="store-dash-api-panel">
      {tab === 'shopify' && (
      <form className="store-dash-connect" onSubmit={connectShopify}>
        <h2>Connect Shopify</h2>
        <p>
          Copy the Client ID and Client secret from the Fluora Dashboard app in the
          Shopify Dev Dashboard. Allowed redirection URL must be exactly{' '}
          <code>https://dashboard.fluoraplant.com/shopify/callback</code>.
          Do not use Open app or open that callback URL yourself.
        </p>
        <label>
          Shop
          <input value={shop} onChange={(event) => setShop(event.target.value)} placeholder="fluora-store.myshopify.com" required />
        </label>
        <label>
          Client ID
          <input value={clientId} onChange={(event) => setClientId(event.target.value)} autoComplete="off" required />
        </label>
        <label>
          Client secret
          <input type="password" value={clientSecret} onChange={(event) => setClientSecret(event.target.value)} autoComplete="off" required />
        </label>
        {sources?.shopify?.configured && !connectError && !sources.shopify.error && (
          <div className="alert alert-success">Shopify is connected.</div>
        )}
        {(connectError || sources?.shopify?.error) && (
          <div className="alert alert-danger">{connectError || sources?.shopify?.error}</div>
        )}
        {authorizeUrl && (
          <p>
            If Shopify says the app is not installed, click{' '}
            <a href={authorizeUrl}>Install on this store</a>, then submit this form again.
          </p>
        )}
        <button type="submit" className="btn btn-dark" disabled={connecting}>
          {connecting ? 'Connecting…' : 'Get access token'}
        </button>
      </form>
      )}

      {tab === 'meta' && (
      <form className="store-dash-connect" onSubmit={connectMeta}>
        <h2>Connect Meta Ads</h2>
        <p>
          Create a token with <strong>ads_read</strong> in{' '}
          <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">Graph API Explorer</a>
          {' '}or a Business Manager system user. Your ad account id is in Ads Manager (<code>act=123</code> in the URL).
        </p>
        <label>
          Access token
          <input type="password" value={metaToken} onChange={(event) => setMetaToken(event.target.value)} autoComplete="off" required />
        </label>
        {metaAccounts.length > 0 ? (
          <label>
            Ad account
            <select value={metaAccountId} onChange={(event) => setMetaAccountId(event.target.value)} required>
              {metaAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} ({account.id})
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Ad account ID <span className="store-dash-optional">optional if the token only sees one</span>
            <input value={metaAccountId} onChange={(event) => setMetaAccountId(event.target.value)} placeholder="act_123456789" autoComplete="off" />
          </label>
        )}
        {metaError && <div className="alert alert-danger">{metaError}</div>}
        <button type="submit" className="btn btn-dark" disabled={metaConnecting}>
          {metaConnecting ? 'Connecting…' : metaAccounts.length ? 'Save ad account' : 'Connect Meta'}
        </button>
      </form>
      )}

      {tab === 'google' && (
      <form className="store-dash-connect" onSubmit={connectGoogle}>
        <h2>Connect Google Ads</h2>
        <p>
          Enable the Google Ads API on the Fluora Dashboard Cloud project and apply for Explorer access.
          Customer ID is the Fluora ads account, not the MCC. If you enter the manager
          (176-815-9611), we’ll list the client accounts under it. Create an OAuth client in that
          same Cloud project, then get a refresh token in{' '}
          <a href="https://developers.google.com/oauthplayground/" target="_blank" rel="noreferrer">OAuth Playground</a>
          {' '}with scope <code>https://www.googleapis.com/auth/adwords</code>.
          Developer tokens are no longer used.
        </p>
        {googleAccounts.length > 0 ? (
          <label>
            Ads account
            <select value={googleCustomerId} onChange={(event) => setGoogleCustomerId(event.target.value)} required>
              {googleAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name ? `${account.name} (${account.id})` : account.id}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Customer ID
            <input value={googleCustomerId} onChange={(event) => setGoogleCustomerId(event.target.value)} placeholder="123-456-7890" />
          </label>
        )}
        <label>
          Manager account ID <span className="store-dash-optional">only if you use an MCC</span>
          <input value={googleLoginCustomerId} onChange={(event) => setGoogleLoginCustomerId(event.target.value)} placeholder="optional" />
        </label>
        <label>
          Developer token <span className="store-dash-optional">optional, unused since 2026</span>
          <input type="password" value={googleDeveloperToken} onChange={(event) => setGoogleDeveloperToken(event.target.value)} autoComplete="off" />
        </label>
        <label>
          OAuth client ID
          <input value={googleClientId} onChange={(event) => setGoogleClientId(event.target.value)} autoComplete="off" required />
        </label>
        <label>
          OAuth client secret
          <input type="password" value={googleClientSecret} onChange={(event) => setGoogleClientSecret(event.target.value)} autoComplete="off" required />
        </label>
        <label>
          Refresh token
          <input type="password" value={googleRefreshToken} onChange={(event) => setGoogleRefreshToken(event.target.value)} autoComplete="off" required />
        </label>
        {googleError && <div className="alert alert-danger">{googleError}</div>}
        <button type="submit" className="btn btn-dark" disabled={googleConnecting}>
          {googleConnecting ? 'Connecting…' : googleAccounts.length ? 'Save ads account' : 'Connect Google Ads'}
        </button>
      </form>
      )}

      {tab === 'gorgias' && (
      <form className="store-dash-connect" onSubmit={connectGorgias}>
        <h2>Connect Gorgias</h2>
        <p>
          Create an API key in Gorgias under Settings → REST API.
          Use the helpdesk subdomain, for example <code>fluora</code> for fluora.gorgias.com,
          plus the email of the user who owns that key.
        </p>
        <label>
          Domain
          <input value={gorgiasDomain} onChange={(event) => setGorgiasDomain(event.target.value)} placeholder="fluora" required />
        </label>
        <label>
          Email
          <input type="email" value={gorgiasEmail} onChange={(event) => setGorgiasEmail(event.target.value)} autoComplete="off" required />
        </label>
        <label>
          API key
          <input type="password" value={gorgiasApiKey} onChange={(event) => setGorgiasApiKey(event.target.value)} autoComplete="off" required />
        </label>
        {gorgiasError && <div className="alert alert-danger">{gorgiasError}</div>}
        <button type="submit" className="btn btn-dark" disabled={gorgiasConnecting}>
          {gorgiasConnecting ? 'Connecting…' : 'Connect Gorgias'}
        </button>
      </form>
      )}

      {tab === 'linear' && (
      <form className="store-dash-connect" onSubmit={connectLinear}>
        <h2>Connect Linear</h2>
        <p>
          Create a personal API key in Linear under Settings → Security &amp; access.
          One key covers the whole workspace. Work shows every team.
        </p>
        <label>
          API key
          <input type="password" value={linearApiKey} onChange={(event) => setLinearApiKey(event.target.value)} autoComplete="off" required />
        </label>
        {linearError && <div className="alert alert-danger">{linearError}</div>}
        <button type="submit" className="btn btn-dark" disabled={linearConnecting}>
          {linearConnecting ? 'Connecting…' : 'Connect Linear'}
        </button>
      </form>
      )}

      {tab === 'sentry' && (
      <form className="store-dash-connect" onSubmit={connectSentry}>
        <h2>Connect Sentry</h2>
        <p>
          Create an Internal Integration in Sentry under Settings → Developer Settings.
          Permissions: Organization Read, Project Read, Issue & Event Read.
          Copy the token from the Tokens section, not the client secret.
          Organization slug is <code>color-and-light</code>.
        </p>
        <label>
          Auth token
          <input type="password" value={sentryToken} onChange={(event) => setSentryToken(event.target.value)} autoComplete="off" required />
        </label>
        {sentryOrgs.length > 0 ? (
          <label>
            Organization
            <select value={sentryOrg} onChange={(event) => setSentryOrg(event.target.value)} required>
              {sentryOrgs.map((org) => (
                <option key={org.slug} value={org.slug}>{org.name}</option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Organization slug
            <input value={sentryOrg} onChange={(event) => setSentryOrg(event.target.value)} placeholder="color-and-light" autoComplete="off" required />
          </label>
        )}
        {sentryError && <div className="alert alert-danger">{sentryError}</div>}
        <button type="submit" className="btn btn-dark" disabled={sentryConnecting}>
          {sentryConnecting ? 'Connecting…' : sentryOrgs.length ? 'Save organization' : 'Connect Sentry'}
        </button>
      </form>
      )}

      {tab === 'facebook' && (
      <form className="store-dash-connect" onSubmit={connectFacebook}>
        <h2>Connect Facebook</h2>
        <p>
          In{' '}
          <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">Graph API Explorer</a>
          {' '}create a user token with <code>pages_show_list</code>, <code>pages_read_engagement</code>,
          <code>read_insights</code>, <code>instagram_basic</code>, and <code>instagram_manage_insights</code>.
          Instagram is read from the Page’s linked professional account. This is separate from Meta Ads.
        </p>
        <label>
          Access token
          <input type="password" value={facebookToken} onChange={(event) => setFacebookToken(event.target.value)} autoComplete="off" required />
        </label>
        {facebookPages.length > 0 ? (
          <label>
            Page
            <select value={facebookPageId} onChange={(event) => setFacebookPageId(event.target.value)} required>
              {facebookPages.map((page) => (
                <option key={page.id} value={page.id}>
                  {page.name}{page.igUsername ? ` · @${page.igUsername}` : ''}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Page ID <span className="store-dash-optional">optional if the token only sees one</span>
            <input value={facebookPageId} onChange={(event) => setFacebookPageId(event.target.value)} placeholder="optional" autoComplete="off" />
          </label>
        )}
        {facebookError && <div className="alert alert-danger">{facebookError}</div>}
        <button type="submit" className="btn btn-dark" disabled={facebookConnecting}>
          {facebookConnecting ? 'Connecting…' : facebookPages.length ? 'Save page' : 'Connect Facebook'}
        </button>
      </form>
      )}

      {tab === 'instagram' && (
      <div className="store-dash-connect">
        <h2>Instagram</h2>
        <p>
          Instagram uses the Facebook Page connection. Link the Fluora Instagram professional account
          to the Facebook Page, then connect Facebook with Instagram insight permissions.
        </p>
        {sources?.instagram?.configured ? (
          <div className="alert alert-success">Instagram is connected through the Facebook Page.</div>
        ) : (
          <p className="store-dash-hint">Connect Facebook first, then pick the Page that has Instagram attached.</p>
        )}
      </div>
      )}

      {tab === 'tiktok' && (
      <form className="store-dash-connect" onSubmit={connectTikTok}>
        <h2>Connect TikTok</h2>
        <p>
          Create a Login Kit app at{' '}
          <a href="https://developers.tiktok.com/" target="_blank" rel="noreferrer">TikTok for Developers</a>
          {' '}and authorize the Fluora account with <code>user.info.basic</code>, <code>user.info.profile</code>,
          <code>user.info.stats</code>, and <code>video.list</code>. Paste the user access token.
          Display API does not provide follower gain/loss.
        </p>
        <label>
          Access token
          <input type="password" value={tiktokToken} onChange={(event) => setTiktokToken(event.target.value)} autoComplete="off" required />
        </label>
        {tiktokError && <div className="alert alert-danger">{tiktokError}</div>}
        <button type="submit" className="btn btn-dark" disabled={tiktokConnecting}>
          {tiktokConnecting ? 'Connecting…' : 'Connect TikTok'}
        </button>
      </form>
      )}

      {tab === 'youtube' && (
      <form className="store-dash-connect" onSubmit={connectYouTube}>
        <h2>Connect YouTube</h2>
        <p>
          Enable YouTube Data API v3 and YouTube Analytics API on the Fluora Dashboard Cloud project.
          Create an OAuth client, then get a refresh token in{' '}
          <a href="https://developers.google.com/oauthplayground/" target="_blank" rel="noreferrer">OAuth Playground</a>
          {' '}with <code>https://www.googleapis.com/auth/youtube.readonly</code> and{' '}
          <code>https://www.googleapis.com/auth/yt-analytics.readonly</code>.
        </p>
        <label>
          OAuth client ID
          <input value={youtubeClientId} onChange={(event) => setYoutubeClientId(event.target.value)} autoComplete="off" required />
        </label>
        <label>
          OAuth client secret
          <input type="password" value={youtubeClientSecret} onChange={(event) => setYoutubeClientSecret(event.target.value)} autoComplete="off" required />
        </label>
        <label>
          Refresh token
          <input type="password" value={youtubeRefreshToken} onChange={(event) => setYoutubeRefreshToken(event.target.value)} autoComplete="off" required />
        </label>
        {youtubeChannels.length > 0 ? (
          <label>
            Channel
            <select value={youtubeChannelId} onChange={(event) => setYoutubeChannelId(event.target.value)} required>
              {youtubeChannels.map((channel) => (
                <option key={channel.id} value={channel.id}>{channel.name}</option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Channel ID <span className="store-dash-optional">optional if the login only sees one</span>
            <input value={youtubeChannelId} onChange={(event) => setYoutubeChannelId(event.target.value)} placeholder="optional" autoComplete="off" />
          </label>
        )}
        {youtubeError && <div className="alert alert-danger">{youtubeError}</div>}
        <button type="submit" className="btn btn-dark" disabled={youtubeConnecting}>
          {youtubeConnecting ? 'Connecting…' : youtubeChannels.length ? 'Save channel' : 'Connect YouTube'}
        </button>
      </form>
      )}
      </div>
      </div>
    </div>
  );
};

export default StoreDashboardApisView;
